import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { generateLoanProgramRecommendations } from '@/utils/supreme-intelligence/loanProgramScoring';
import { EnhancedUnifiedFormData } from '@/validation/enhancedUnifiedSchemas';
import { CheckCircle2, XCircle, ChevronDown, ChevronUp } from 'lucide-react';

interface LoanProgramRecommenderProps {
  formData: EnhancedUnifiedFormData;
  baseMonthlyPayment: number;
}

export const LoanProgramRecommender: React.FC<LoanProgramRecommenderProps> = ({
  formData,
  baseMonthlyPayment
}) => {
  const recommendations = generateLoanProgramRecommendations(formData, baseMonthlyPayment);
  const [expandedProgram, setExpandedProgram] = useState<string | null>(recommendations[0]?.programType || null);

  const getConfidenceBadgeColor = (score: number) => {
    if (score >= 80) return 'bg-green-500 text-white';
    if (score >= 60) return 'bg-yellow-500 text-white';
    if (score >= 40) return 'bg-orange-500 text-white';
    return 'bg-red-500 text-white';
  };

  const getComplexityBadge = (complexity: string) => {
    const colors = {
      simple: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      moderate: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
      complex: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
    };
    return colors[complexity as keyof typeof colors] || colors.moderate;
  };

  return (
    <div className="space-y-4">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-foreground mb-2">Loan Program Recommendations</h3>
        <p className="text-sm text-muted-foreground">
          Based on your financial profile, here are the best mortgage options ranked by confidence score
        </p>
      </div>

      {recommendations.map((rec, idx) => (
        <Card key={rec.programType} className="overflow-hidden">
          <div 
            className="p-4 cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={() => setExpandedProgram(expandedProgram === rec.programType ? null : rec.programType)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Badge className={getConfidenceBadgeColor(rec.confidenceScore)}>
                  {rec.confidenceScore}% Match
                </Badge>
                <div>
                  <h4 className="font-semibold text-foreground">{rec.programType.toUpperCase()} Loan</h4>
                  <p className="text-sm text-muted-foreground">{rec.timeline} typical timeline</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <Badge className={getComplexityBadge(rec.complexity)}>
                  {rec.complexity}
                </Badge>
                {expandedProgram === rec.programType ? <ChevronUp /> : <ChevronDown />}
              </div>
            </div>
          </div>

          {expandedProgram === rec.programType && (
            <div className="p-4 pt-0 space-y-4 border-t border-border">
              <div className="grid md:grid-cols-3 gap-4 mb-4">
                <div>
                  <div className="text-sm text-muted-foreground">Monthly Payment</div>
                  <div className="text-lg font-bold text-foreground">${rec.monthlyPayment.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Down Payment</div>
                  <div className="text-lg font-bold text-foreground">${rec.downPaymentRequired.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Interest Rate</div>
                  <div className="text-lg font-bold text-foreground">{rec.interestRate.toFixed(2)}%</div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h5 className="font-medium text-foreground mb-2 flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    Pros
                  </h5>
                  <ul className="space-y-1">
                    {rec.pros.map((pro, i) => (
                      <li key={i} className="text-sm text-muted-foreground">• {pro}</li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h5 className="font-medium text-foreground mb-2 flex items-center gap-2">
                    <XCircle className="h-4 w-4 text-red-500" />
                    Cons
                  </h5>
                  <ul className="space-y-1">
                    {rec.cons.map((con, i) => (
                      <li key={i} className="text-sm text-muted-foreground">• {con}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div>
                <h5 className="font-medium text-foreground mb-2">Ideal For</h5>
                <div className="flex flex-wrap gap-2">
                  {rec.idealFor.map((ideal, i) => (
                    <Badge key={i} variant="secondary">{ideal}</Badge>
                  ))}
                </div>
              </div>

              <div>
                <h5 className="font-medium text-foreground mb-2">Requirements</h5>
                <ul className="space-y-1">
                  {rec.requirements.map((req, i) => (
                    <li key={i} className="text-sm text-muted-foreground">• {req}</li>
                  ))}
                </ul>
              </div>

              <div className="pt-4 border-t border-border">
                <div className="text-xs text-muted-foreground mb-2">Total Cost Over 30 Years</div>
                <div className="text-2xl font-bold text-foreground">${rec.totalCostOverLife.toLocaleString()}</div>
              </div>
            </div>
          )}
        </Card>
      ))}
    </div>
  );
};
